// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  MapPinIcon,
  PhoneIcon,
  UserGroupIcon,
  TruckIcon,
  CubeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  Squares2X2Icon,
  TableCellsIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { Site, mockSites as initialSites, getCityCoordinates } from '../data/sitesData';
import { useSiteRestriction } from '../hooks/useSiteRestriction';
import { sitesAPI } from '../services/api';

const Sites: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const cameFromAlerts = useRef(false);

  // Site restriction hook for Site Managers
  const { isRestricted, restrictedSiteId } = useSiteRestriction();

  // Filter initial sites based on restriction
  const allowedInitialSites = useMemo(() => {
    if (isRestricted && restrictedSiteId) {
      return initialSites.filter(site => site.id === restrictedSiteId);
    }
    return initialSites;
  }, [isRestricted, restrictedSiteId]);

  const [sites, setSites] = useState<Site[]>(allowedInitialSites);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirmSite, setDeleteConfirmSite] = useState<Site | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const itemsPerPage = 6;

  // Fetch sites from API on mount
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await sitesAPI.list();
        if (response.data && Array.isArray(response.data)) {
          let fetchedSites = response.data;
          // Apply site restriction if needed
          if (isRestricted && restrictedSiteId) {
            fetchedSites = fetchedSites.filter((site: Site) => site.id === restrictedSiteId);
          }
          setSites(fetchedSites);
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
        // Keep initial sites as fallback
        setSites(allowedInitialSites);
      }
    };
    fetchSites();
  }, [isRestricted, restrictedSiteId, allowedInitialSites]);

  // Handle URL query params for deep linking from Alerts
  useEffect(() => {
    const siteId = searchParams.get('site');
    if (siteId) {
      // Search by both id and code since alerts may use either format
      const site = sites.find(s => s.id === siteId || s.code === siteId || s.code.includes(siteId) || siteId.includes(s.code));
      if (site) {
        cameFromAlerts.current = true;
        setSelectedSite(site);
        // Clear the query param after handling
        setSearchParams({});
      }
    }
  }, [searchParams, setSearchParams, sites]);

  // Handle closing site detail modal - return to Alerts if came from there
  const handleCloseSiteDetail = () => {
    setSelectedSite(null);
    if (cameFromAlerts.current) {
      cameFromAlerts.current = false;
      navigate('/alerts');
    }
  };

  // Add Site form state
  const [newSite, setNewSite] = useState({
    name: '',
    code: '',
    type: 'warehouse' as Site['type'],
    status: 'operational' as Site['status'],
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA',
    manager: '',
    phone: '',
    email: '',
    totalCapacity: '',
    employees: '',
    inboundDocks: '',
    outboundDocks: '',
    region: 'Midwest',
    operatingHours: '24/7',
  });

  const resetNewSiteForm = () => {
    setNewSite({
      name: '',
      code: '',
      type: 'warehouse',
      status: 'operational',
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'USA',
      manager: '',
      phone: '',
      email: '',
      totalCapacity: '',
      employees: '',
      inboundDocks: '',
      outboundDocks: '',
      region: 'Midwest',
      operatingHours: '24/7',
    });
  };

  const handleAddSite = async () => {
    const newId = `SITE${String(sites.length + 1).padStart(3, '0')}`;
    const totalCap = parseInt(newSite.totalCapacity) || 100000;

    // Get coordinates based on city and state
    const coordinates = getCityCoordinates(newSite.city, newSite.state);

    const siteData = {
      id: newId,
      name: newSite.name,
      code: newSite.code || `${newSite.city.substring(0, 3).toUpperCase()}-${newSite.type.substring(0, 2).toUpperCase()}-01`,
      type: newSite.type,
      status: newSite.status,
      address_street: newSite.street,
      address_city: newSite.city,
      address_state: newSite.state,
      address_zip: newSite.zip,
      address_country: newSite.country,
      latitude: coordinates.lat,
      longitude: coordinates.lng,
      contact_manager: newSite.manager,
      contact_phone: newSite.phone,
      contact_email: newSite.email,
      capacity_total: totalCap,
      capacity_used: Math.round(totalCap * 0.6),
      capacity_unit: 'sq ft',
      employees: parseInt(newSite.employees) || 100,
      docks_inbound: parseInt(newSite.inboundDocks) || 10,
      docks_outbound: parseInt(newSite.outboundDocks) || 10,
      shipments_today_inbound: Math.floor(Math.random() * 100) + 50,
      shipments_today_outbound: Math.floor(Math.random() * 150) + 50,
      avg_processing_time: 4.0,
      region: newSite.region,
      certifications: ['ISO 9001'],
      operating_hours: newSite.operatingHours,
      last_inspection: new Date().toISOString().split('T')[0],
    };

    try {
      const response = await sitesAPI.create(siteData);
      if (response.data) {
        setSites([...sites, response.data]);
      }
    } catch (error) {
      console.error('Error creating site:', error);
    }

    setShowAddModal(false);
    resetNewSiteForm();
  };

  const handleDeleteSite = async (site: Site) => {
    try {
      await sitesAPI.delete(site.id);
      setSites(sites.filter(s => s.id !== site.id));
      if (selectedSite?.id === site.id) {
        setSelectedSite(null);
      }
    } catch (error) {
      console.error('Error deleting site:', error);
    }
    setDeleteConfirmSite(null);
  };

  const startEditing = () => {
    if (selectedSite) {
      setEditingSite({ ...selectedSite });
      setIsEditing(true);
    }
  };

  const cancelEditing = () => {
    setEditingSite(null);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (editingSite) {
      // Convert Site object to flat database format for API
      const updateData = {
        name: editingSite.name,
        code: editingSite.code,
        type: editingSite.type,
        status: editingSite.status,
        address_street: editingSite.address.street,
        address_city: editingSite.address.city,
        address_state: editingSite.address.state,
        address_zip: editingSite.address.zip,
        address_country: editingSite.address.country,
        latitude: editingSite.coordinates.lat,
        longitude: editingSite.coordinates.lng,
        contact_manager: editingSite.contact.manager,
        contact_phone: editingSite.contact.phone,
        contact_email: editingSite.contact.email,
        capacity_total: editingSite.capacity.total,
        capacity_used: editingSite.capacity.used,
        employees: editingSite.employees,
        docks_inbound: editingSite.docks.inbound,
        docks_outbound: editingSite.docks.outbound,
        region: editingSite.region,
        operating_hours: editingSite.operatingHours,
      };

      try {
        await sitesAPI.update(editingSite.id, updateData);
        setSites(sites.map(s => s.id === editingSite.id ? editingSite : s));
        setSelectedSite(editingSite);
      } catch (error) {
        console.error('Error updating site:', error);
      }

      setEditingSite(null);
      setIsEditing(false);
    }
  };

  const updateEditingSite = (updates: Partial<Site>) => {
    if (editingSite) {
      setEditingSite({ ...editingSite, ...updates });
    }
  };

  const getStatusColor = (status: Site['status']) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'limited':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'offline':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: Site['status']) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'maintenance':
        return <ClockIcon className="w-4 h-4" />;
      case 'limited':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'offline':
        return <XMarkIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getTypeLabel = (type: Site['type']) => {
    switch (type) {
      case 'warehouse':
        return 'Warehouse';
      case 'distribution_center':
        return 'Distribution Center';
      case 'cross_dock':
        return 'Cross-Dock';
      case 'fulfillment_center':
        return 'Fulfillment Center';
      case 'cold_storage':
        return 'Cold Storage';
      default:
        return type;
    }
  };

  const getCapacityPercentage = (site: Site) => {
    return Math.round((site.capacity.used / site.capacity.total) * 100);
  };

  const getCapacityColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-teal-500';
  };

  const filteredSites = sites.filter((site) => {
    const matchesSearch =
      site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.address.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || site.status === statusFilter;
    const matchesType = typeFilter === 'all' || site.type === typeFilter;
    const matchesRegion = regionFilter === 'all' || site.region === regionFilter;
    return matchesSearch && matchesStatus && matchesType && matchesRegion;
  });

  const totalPages = Math.ceil(filteredSites.length / itemsPerPage);
  const paginatedSites = filteredSites.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Summary stats
  const totalCapacity = sites.reduce((sum, s) => sum + s.capacity.total, 0);
  const usedCapacity = sites.reduce((sum, s) => sum + s.capacity.used, 0);
  const totalEmployees = sites.reduce((sum, s) => sum + s.employees, 0);
  const totalShipmentsToday = sites.reduce(
    (sum, s) => sum + s.shipmentsToday.inbound + s.shipmentsToday.outbound,
    0
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sites</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage warehouses, distribution centers, and fulfillment facilities
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Add Site
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <BuildingOffice2Icon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Sites</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{sites.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <CubeIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Capacity</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {(totalCapacity / 1000000).toFixed(2)}M sq ft
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {Math.round((usedCapacity / totalCapacity) * 100)}% utilized
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <UserGroupIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Employees</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalEmployees.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <TruckIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Shipments Today</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalShipmentsToday.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search sites by name, code, or city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Status</option>
                <option value="operational">Operational</option>
                <option value="maintenance">Maintenance</option>
                <option value="limited">Limited</option>
                <option value="offline">Offline</option>
              </select>
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Types</option>
              <option value="warehouse">Warehouse</option>
              <option value="distribution_center">Distribution Center</option>
              <option value="fulfillment_center">Fulfillment Center</option>
              <option value="cross_dock">Cross-Dock</option>
              <option value="cold_storage">Cold Storage</option>
            </select>
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Regions</option>
              <option value="Midwest">Midwest</option>
              <option value="Northeast">Northeast</option>
              <option value="Southeast">Southeast</option>
              <option value="Southwest">Southwest</option>
              <option value="West Coast">West Coast</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex gap-1 border border-gray-300 dark:border-gray-600 rounded-lg p-1 bg-white dark:bg-gray-700">
              <div className="relative group">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                  aria-label="Card View"
                >
                  <Squares2X2Icon className="w-4 h-4" />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  Card View
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                </div>
              </div>
              <div className="relative group">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                  aria-label="List View"
                >
                  <TableCellsIcon className="w-4 h-4" />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  List View
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sites Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {paginatedSites.map((site) => {
            const capacityPercent = getCapacityPercentage(site);
            return (
              <div
                key={site.id}
                onClick={() => setSelectedSite(site)}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                        <BuildingOffice2Icon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{site.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{site.code}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          site.status
                        )}`}
                      >
                        {getStatusIcon(site.status)}
                        {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmSite(site);
                        }}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete site"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Type & Location */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <CubeIcon className="w-4 h-4" />
                      <span>{getTypeLabel(site.type)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <MapPinIcon className="w-4 h-4" />
                      <span>
                        {site.address.city}, {site.address.state}
                      </span>
                    </div>
                  </div>

                  {/* Capacity Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Capacity</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {capacityPercent}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getCapacityColor(capacityPercent)} transition-all`}
                        style={{ width: `${capacityPercent}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {site.capacity.used.toLocaleString()} / {site.capacity.total.toLocaleString()}{' '}
                      {site.capacity.unit}
                    </p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div className="text-center">
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {site.employees}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Employees</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {site.docks.inbound + site.docks.outbound}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Docks</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {site.shipmentsToday.inbound + site.shipmentsToday.outbound}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Today</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Site
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Capacity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Shipments Today
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedSites.map((site) => {
                const capacityPercent = getCapacityPercentage(site);
                return (
                  <tr
                    key={site.id}
                    onClick={() => setSelectedSite(site)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                          <BuildingOffice2Icon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{site.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{site.code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {getTypeLabel(site.type)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {site.address.city}, {site.address.state}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          site.status
                        )}`}
                      >
                        {getStatusIcon(site.status)}
                        {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="w-24">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-600 dark:text-gray-400">{capacityPercent}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${getCapacityColor(capacityPercent)}`}
                            style={{ width: `${capacityPercent}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <span className="text-green-600 dark:text-green-400">
                          ↓ {site.shipmentsToday.inbound}
                        </span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span className="text-blue-600 dark:text-blue-400">
                          ↑ {site.shipmentsToday.outbound}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmSite(site);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete site"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, filteredSites.length)} of {filteredSites.length}{' '}
            sites
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 rounded-lg text-sm font-medium ${
                  currentPage === page
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Site Detail Modal */}
      {selectedSite && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-xl">
                  {isEditing ? (
                    <PencilIcon className="w-8 h-8 text-teal-600 dark:text-teal-400" />
                  ) : (
                    <BuildingOffice2Icon className="w-8 h-8 text-teal-600 dark:text-teal-400" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {isEditing ? 'Edit Site' : selectedSite.name}
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400">
                    {isEditing ? editingSite?.name : selectedSite.code}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  if (isEditing) {
                    cancelEditing();
                  } else {
                    handleCloseSiteDetail();
                  }
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {isEditing && editingSite ? (
                /* Edit Mode */
                <>
                  {/* Basic Info */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Site Name
                        </label>
                        <input
                          type="text"
                          value={editingSite.name}
                          onChange={(e) => updateEditingSite({ name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Site Code
                        </label>
                        <input
                          type="text"
                          value={editingSite.code}
                          onChange={(e) => updateEditingSite({ code: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Type
                        </label>
                        <select
                          value={editingSite.type}
                          onChange={(e) => updateEditingSite({ type: e.target.value as Site['type'] })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="warehouse">Warehouse</option>
                          <option value="distribution_center">Distribution Center</option>
                          <option value="fulfillment_center">Fulfillment Center</option>
                          <option value="cross_dock">Cross-Dock</option>
                          <option value="cold_storage">Cold Storage</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Status
                        </label>
                        <select
                          value={editingSite.status}
                          onChange={(e) => updateEditingSite({ status: e.target.value as Site['status'] })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="operational">Operational</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="limited">Limited</option>
                          <option value="offline">Offline</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                      Address
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Street Address
                        </label>
                        <input
                          type="text"
                          value={editingSite.address.street}
                          onChange={(e) => updateEditingSite({ address: { ...editingSite.address, street: e.target.value } })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            City
                          </label>
                          <input
                            type="text"
                            value={editingSite.address.city}
                            onChange={(e) => updateEditingSite({ address: { ...editingSite.address, city: e.target.value } })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            State
                          </label>
                          <input
                            type="text"
                            value={editingSite.address.state}
                            onChange={(e) => updateEditingSite({ address: { ...editingSite.address, state: e.target.value } })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            ZIP Code
                          </label>
                          <input
                            type="text"
                            value={editingSite.address.zip}
                            onChange={(e) => updateEditingSite({ address: { ...editingSite.address, zip: e.target.value } })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Region
                          </label>
                          <select
                            value={editingSite.region}
                            onChange={(e) => updateEditingSite({ region: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                          >
                            <option value="Midwest">Midwest</option>
                            <option value="Northeast">Northeast</option>
                            <option value="Southeast">Southeast</option>
                            <option value="Southwest">Southwest</option>
                            <option value="West Coast">West Coast</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Manager Name
                        </label>
                        <input
                          type="text"
                          value={editingSite.contact.manager}
                          onChange={(e) => updateEditingSite({ contact: { ...editingSite.contact, manager: e.target.value } })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Phone
                        </label>
                        <input
                          type="text"
                          value={editingSite.contact.phone}
                          onChange={(e) => updateEditingSite({ contact: { ...editingSite.contact, phone: e.target.value } })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={editingSite.contact.email}
                          onChange={(e) => updateEditingSite({ contact: { ...editingSite.contact, email: e.target.value } })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Capacity & Operations */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                      Capacity & Operations
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Total Capacity (sq ft)
                        </label>
                        <input
                          type="number"
                          value={editingSite.capacity.total}
                          onChange={(e) => updateEditingSite({ capacity: { ...editingSite.capacity, total: parseInt(e.target.value) || 0 } })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Used Capacity (sq ft)
                        </label>
                        <input
                          type="number"
                          value={editingSite.capacity.used}
                          onChange={(e) => updateEditingSite({ capacity: { ...editingSite.capacity, used: parseInt(e.target.value) || 0 } })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Employees
                        </label>
                        <input
                          type="number"
                          value={editingSite.employees}
                          onChange={(e) => updateEditingSite({ employees: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Operating Hours
                        </label>
                        <select
                          value={editingSite.operatingHours}
                          onChange={(e) => updateEditingSite({ operatingHours: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="24/7">24/7</option>
                          <option value="5:00 AM - 11:00 PM">5:00 AM - 11:00 PM</option>
                          <option value="6:00 AM - 10:00 PM">6:00 AM - 10:00 PM</option>
                          <option value="7:00 AM - 7:00 PM">7:00 AM - 7:00 PM</option>
                          <option value="8:00 AM - 6:00 PM">8:00 AM - 6:00 PM</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Inbound Docks
                        </label>
                        <input
                          type="number"
                          value={editingSite.docks.inbound}
                          onChange={(e) => updateEditingSite({ docks: { ...editingSite.docks, inbound: parseInt(e.target.value) || 0 } })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Outbound Docks
                        </label>
                        <input
                          type="number"
                          value={editingSite.docks.outbound}
                          onChange={(e) => updateEditingSite({ docks: { ...editingSite.docks, outbound: parseInt(e.target.value) || 0 } })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* View Mode */
                <>
                  {/* Status & Type Row */}
                  <div className="flex flex-wrap gap-3">
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(
                        selectedSite.status
                      )}`}
                    >
                      {getStatusIcon(selectedSite.status)}
                      {selectedSite.status.charAt(0).toUpperCase() + selectedSite.status.slice(1)}
                    </span>
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      {getTypeLabel(selectedSite.type)}
                    </span>
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      {selectedSite.region}
                    </span>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Location */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                        Location
                      </h3>
                      <div className="flex items-start gap-3">
                        <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>{selectedSite.address.street}</p>
                          <p>
                            {selectedSite.address.city}, {selectedSite.address.state}{' '}
                            {selectedSite.address.zip}
                          </p>
                          <p>{selectedSite.address.country}</p>
                        </div>
                      </div>
                    </div>

                    {/* Contact */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                        Contact
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <UserGroupIcon className="w-5 h-5 text-gray-400" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {selectedSite.contact.manager}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <PhoneIcon className="w-5 h-5 text-gray-400" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {selectedSite.contact.phone}
                          </span>
                        </div>
                        <p className="text-sm text-teal-600 dark:text-teal-400 ml-8">
                          {selectedSite.contact.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Capacity */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Capacity Utilization
                      </h3>
                      <span className="text-2xl font-bold text-gray-900 dark:text-white">
                        {getCapacityPercentage(selectedSite)}%
                      </span>
                    </div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden mb-2">
                      <div
                        className={`h-full ${getCapacityColor(getCapacityPercentage(selectedSite))} transition-all`}
                        style={{ width: `${getCapacityPercentage(selectedSite)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>
                        Used: {selectedSite.capacity.used.toLocaleString()} {selectedSite.capacity.unit}
                      </span>
                      <span>
                        Total: {selectedSite.capacity.total.toLocaleString()} {selectedSite.capacity.unit}
                      </span>
                    </div>
                  </div>

                  {/* Operations Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {selectedSite.employees}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Employees</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {selectedSite.docks.inbound} / {selectedSite.docks.outbound}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Docks (In/Out)</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {selectedSite.avgProcessingTime}h
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Avg Processing</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {selectedSite.shipmentsToday.inbound + selectedSite.shipmentsToday.outbound}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Shipments Today</p>
                    </div>
                  </div>

                  {/* Shipments Breakdown */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TruckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-800 dark:text-green-400">
                          Inbound
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                        {selectedSite.shipmentsToday.inbound}
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-500">shipments today</p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TruckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 transform rotate-180" />
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-400">
                          Outbound
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                        {selectedSite.shipmentsToday.outbound}
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-500">shipments today</p>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                        Certifications
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedSite.certifications.map((cert) => (
                          <span
                            key={cert}
                            className="px-2 py-1 bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400 rounded text-xs font-medium"
                          >
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Operating Hours</span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {selectedSite.operatingHours}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Last Inspection</span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {new Date(selectedSite.lastInspection).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              {isEditing ? (
                <>
                  <button
                    onClick={cancelEditing}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleCloseSiteDetail}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={startEditing}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                    Edit Site
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Site Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <PlusIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add New Site</h2>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetNewSiteForm();
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Site Name *
                    </label>
                    <input
                      type="text"
                      value={newSite.name}
                      onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                      placeholder="e.g., Denver Distribution Center"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Site Code
                    </label>
                    <input
                      type="text"
                      value={newSite.code}
                      onChange={(e) => setNewSite({ ...newSite, code: e.target.value })}
                      placeholder="e.g., DEN-DC-01 (auto-generated if empty)"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type *
                    </label>
                    <select
                      value={newSite.type}
                      onChange={(e) => setNewSite({ ...newSite, type: e.target.value as Site['type'] })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="warehouse">Warehouse</option>
                      <option value="distribution_center">Distribution Center</option>
                      <option value="fulfillment_center">Fulfillment Center</option>
                      <option value="cross_dock">Cross-Dock</option>
                      <option value="cold_storage">Cold Storage</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <select
                      value={newSite.status}
                      onChange={(e) => setNewSite({ ...newSite, status: e.target.value as Site['status'] })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="operational">Operational</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="limited">Limited</option>
                      <option value="offline">Offline</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                  Address
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      value={newSite.street}
                      onChange={(e) => setNewSite({ ...newSite, street: e.target.value })}
                      placeholder="e.g., 1234 Industrial Blvd"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        City *
                      </label>
                      <input
                        type="text"
                        value={newSite.city}
                        onChange={(e) => setNewSite({ ...newSite, city: e.target.value })}
                        placeholder="Denver"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        State *
                      </label>
                      <input
                        type="text"
                        value={newSite.state}
                        onChange={(e) => setNewSite({ ...newSite, state: e.target.value })}
                        placeholder="CO"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        value={newSite.zip}
                        onChange={(e) => setNewSite({ ...newSite, zip: e.target.value })}
                        placeholder="80202"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Region
                      </label>
                      <select
                        value={newSite.region}
                        onChange={(e) => setNewSite({ ...newSite, region: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="Midwest">Midwest</option>
                        <option value="Northeast">Northeast</option>
                        <option value="Southeast">Southeast</option>
                        <option value="Southwest">Southwest</option>
                        <option value="West Coast">West Coast</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Manager Name
                    </label>
                    <input
                      type="text"
                      value={newSite.manager}
                      onChange={(e) => setNewSite({ ...newSite, manager: e.target.value })}
                      placeholder="John Smith"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      value={newSite.phone}
                      onChange={(e) => setNewSite({ ...newSite, phone: e.target.value })}
                      placeholder="(303) 555-0123"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newSite.email}
                      onChange={(e) => setNewSite({ ...newSite, email: e.target.value })}
                      placeholder="jsmith@auxilio.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>

              {/* Capacity & Operations */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
                  Capacity & Operations
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Total Capacity (sq ft)
                    </label>
                    <input
                      type="number"
                      value={newSite.totalCapacity}
                      onChange={(e) => setNewSite({ ...newSite, totalCapacity: e.target.value })}
                      placeholder="100000"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Employees
                    </label>
                    <input
                      type="number"
                      value={newSite.employees}
                      onChange={(e) => setNewSite({ ...newSite, employees: e.target.value })}
                      placeholder="100"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Inbound Docks
                    </label>
                    <input
                      type="number"
                      value={newSite.inboundDocks}
                      onChange={(e) => setNewSite({ ...newSite, inboundDocks: e.target.value })}
                      placeholder="10"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Outbound Docks
                    </label>
                    <input
                      type="number"
                      value={newSite.outboundDocks}
                      onChange={(e) => setNewSite({ ...newSite, outboundDocks: e.target.value })}
                      placeholder="10"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Operating Hours
                  </label>
                  <select
                    value={newSite.operatingHours}
                    onChange={(e) => setNewSite({ ...newSite, operatingHours: e.target.value })}
                    className="w-full md:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="24/7">24/7</option>
                    <option value="5:00 AM - 11:00 PM">5:00 AM - 11:00 PM</option>
                    <option value="6:00 AM - 10:00 PM">6:00 AM - 10:00 PM</option>
                    <option value="7:00 AM - 7:00 PM">7:00 AM - 7:00 PM</option>
                    <option value="8:00 AM - 6:00 PM">8:00 AM - 6:00 PM</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetNewSiteForm();
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSite}
                disabled={!newSite.name || !newSite.city || !newSite.state || !newSite.zip || !newSite.street}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Site
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmSite && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <TrashIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Site</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to delete <span className="font-semibold">{deleteConfirmSite.name}</span> ({deleteConfirmSite.code})?
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirmSite(null)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteSite(deleteConfirmSite)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Site
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sites;
